import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Star, ChevronRight, Filter, CheckCircle, Globe, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'All States', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska',
  'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas',
  'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

export default function ProvidersPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('All States');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');

  const cpt = searchParams.get('cpt');
  const procedureName = searchParams.get('procedure');

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedState && selectedState !== 'All States') params.state = selectedState;
      if (cpt) params.cpt = cpt;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/providers`, { params });
      setProviders(res.data);
    } catch (e) {
      console.error('Failed to fetch providers', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [selectedState, cpt]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProviders();
  };

  const sortedProviders = useMemo(() => {
    const arr = [...providers];
    if (sortBy === 'rating')     return arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    if (sortBy === 'price_asc')  return arr.sort((a, b) => {
      const pa = a.procedures?.length ? Math.min(...a.procedures.map(p => p.cash_price)) : Infinity;
      const pb = b.procedures?.length ? Math.min(...b.procedures.map(p => p.cash_price)) : Infinity;
      return pa - pb;
    });
    if (sortBy === 'price_desc') return arr.sort((a, b) => {
      const pa = a.procedures?.length ? Math.min(...a.procedures.map(p => p.cash_price)) : 0;
      const pb = b.procedures?.length ? Math.min(...b.procedures.map(p => p.cash_price)) : 0;
      return pb - pa;
    });
    if (sortBy === 'name_az')    return arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return arr;
  }, [providers, sortBy]);

  const getPriceForCpt = (provider, targetCpt) => {
    if (!targetCpt) return null;
    const proc = provider.procedures?.find(p => p.cpt === targetCpt);
    return proc ? proc.cash_price : null;
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-widest uppercase font-semibold text-[#0D3D4A] mb-3">Provider Directory</p>
          <h1 className="text-4xl font-medium text-stone-900 mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
            {procedureName ? `Providers for ${procedureName}` : 'Find Surgical Centers'}
          </h1>
          <p className="text-stone-600">Accredited surgical centers with transparent cash pricing across all 50 states.</p>
          {cpt && (
            <div className="mt-4 inline-flex items-center gap-2 bg-[#F0EEE9] border border-[#0D3D4A]/20 px-4 py-2 text-sm">
              <CheckCircle size={14} className="text-[#0D3D4A]" />
              <span className="text-[#0D3D4A]">Filtering by CPT code: <strong>{cpt}</strong></span>
              <button onClick={() => navigate('/providers')} className="text-[#0D3D4A] hover:text-[#0D3D4A] ml-2 text-xs underline">Clear</button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex gap-0 flex-1 max-w-lg">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by center name or city..."
              className="flex-1 px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
              data-testid="provider-search-input"
            />
            <button type="submit" className="bg-[#0D3D4A] text-white px-5 py-3 text-sm hover:bg-[#0E4557] transition-colors">Search</button>
          </form>

          <select
            value={selectedState}
            onChange={e => setSelectedState(e.target.value)}
            className="px-4 py-3 border border-[#A8B4B8] bg-white text-sm text-stone-900 focus:outline-none focus:border-[#0D3D4A]"
            data-testid="state-filter-select"
          >
            {US_STATES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-400 whitespace-nowrap">Sort:</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="border border-stone-200 px-3 py-3 text-sm text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#0D3D4A]"
              data-testid="provider-sort-select"
            >
              <option value="rating">Rating: High to Low</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="name_az">Name: A to Z</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <p className="text-sm text-stone-500 mb-6" data-testid="provider-count">
          {loading ? 'Loading...' : `${providers.length} provider${providers.length !== 1 ? 's' : ''} found`}
        </p>

        {/* Provider Cards */}
        {loading ? (
          <div className="space-y-4">
            {Array.from({length: 3}).map((_, i) => (
              <div key={i} className="bg-white border border-stone-200 p-8 animate-pulse">
                <div className="h-5 bg-stone-100 rounded w-1/3 mb-4" />
                <div className="h-4 bg-stone-100 rounded w-1/2 mb-3" />
                <div className="h-4 bg-stone-100 rounded w-full" />
              </div>
            ))}
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-20">
            <MapPin size={40} className="text-[#A8B4B8] mx-auto mb-4" />
            <p className="text-stone-500 mb-2">No providers found for your search criteria.</p>
            <p className="text-sm text-[#A8B4B8]">Try expanding your state filter or clearing the CPT code filter.</p>
          </div>
        ) : (
          <div className="space-y-4 animate-stagger">
            {sortedProviders.map(provider => {
              const specificPrice = getPriceForCpt(provider, cpt);
              const lowestPrice = provider.procedures?.length > 0
                ? Math.min(...provider.procedures.map(p => p.cash_price))
                : null;

              return (
                <div
                  key={provider.id}
                  className="bg-white border border-stone-200 p-6 md:p-8 card-hover cursor-pointer"
                  onClick={() => navigate(`/provider/${provider.id}`)}
                  data-testid={`provider-card-${provider.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex-1">
                      <div className="flex items-start gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            <h2 className="text-lg font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>{provider.name}</h2>
                            {provider.featured && (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#0D3D4A] text-white font-semibold px-2 py-0.5" data-testid={`verified-partner-badge-${provider.id}`}>
                                <Award size={11} /> Verified Partner
                              </span>
                            )}
                            {provider.cash_pay_only && (
                              <span className="text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5">Cash Pay Only</span>
                            )}
                            {provider.telehealth && (
                              <span className="text-xs bg-[#F0EEE9] text-[#0D3D4A] border border-[#0D3D4A]/20 px-2 py-0.5">Telehealth</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-stone-500">
                            <span className="flex items-center gap-1"><MapPin size={13} /> {provider.city}, {provider.state_full}</span>
                            <span className="flex items-center gap-1"><Star size={13} className="text-yellow-500 fill-yellow-500" /> {provider.rating} ({provider.reviews} reviews)</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-stone-600 mb-4 leading-relaxed">{provider.about}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {provider.specialties?.map(s => (
                          <span key={s} className="text-xs bg-stone-100 text-stone-600 px-2 py-1">{s}</span>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span className="bg-stone-100 px-2 py-1">{provider.type}</span>
                        <span className="bg-stone-100 px-2 py-1">{provider.accreditation} Accredited</span>
                      </div>
                    </div>

                    <div className="md:text-right md:min-w-[180px]">
                      {specificPrice ? (
                        <div className="mb-4">
                          <p className="text-xs text-[#A8B4B8] mb-1">Cash price for this procedure</p>
                          <p className="text-3xl font-bold text-[#0D3D4A]" data-testid={`provider-price-${provider.id}`}>${specificPrice.toLocaleString()}</p>
                          <p className="text-xs text-[#A8B4B8]">All-inclusive*</p>
                        </div>
                      ) : lowestPrice ? (
                        <div className="mb-4">
                          <p className="text-xs text-[#A8B4B8] mb-1">Procedures from</p>
                          <p className="text-3xl font-bold text-[#0D3D4A]">${lowestPrice.toLocaleString()}</p>
                        </div>
                      ) : null}

                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/provider/${provider.id}`); }}
                        className="bg-[#0D3D4A] text-white px-6 py-3 text-sm font-medium hover:bg-[#0E4557] transition-colors flex items-center gap-2 md:ml-auto"
                        data-testid={`view-provider-${provider.id}`}
                      >
                        View Details <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
